import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from "react";
import type { PlatformApiClient } from "../api";
import { PlatformApiError } from "../api";
import { SalonIcon } from "./SalonIcon";
import { ActionButton, ActionToolbar, OverflowMenu } from "../adaptive/actions";
import type {
  SalonAgenda,
  SalonAgendaAppointment,
  SalonAgendaBlock,
  SalonAgendaResource,
  SalonAppointmentDeposit,
  SalonAppointmentStatus,
  SalonAvailabilitySlot,
  SalonOverview,
  SalonService,
} from "./types";

type AgendaView = "day" | "week" | "list" | "timeline";
type AgendaListSort =
  | "date_asc"
  | "date_desc"
  | "customer_asc"
  | "service_asc"
  | "status_asc";

const HOUR_START = 8;
const HOUR_END = 20;
const MINUTES_IN_DAY_WINDOW = (HOUR_END - HOUR_START) * 60;
const AGENDA_INTERVAL_MINUTES = 30;
const AGENDA_SLOT_HEIGHT_PX = 48;
const TIMELINE_SLOT_WIDTH_PX = 84;

function partsInZone(value: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(value);
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? "0");
  return {
    year: read("year"),
    month: read("month"),
    day: read("day"),
    hour: read("hour"),
    minute: read("minute"),
    second: read("second"),
  };
}

function dateKeyInZone(value: Date | string, timeZone: string): string {
  const date = typeof value === "string" ? new Date(value) : value;
  const parts = partsInZone(date, timeZone);
  return [
    parts.year,
    String(parts.month).padStart(2, "0"),
    String(parts.day).padStart(2, "0"),
  ].join("-");
}

function addDays(dateKey: string, days: number): string {
  const [year, month, day] = dateKey.split("-").map(Number);
  const value = new Date(Date.UTC(year!, month! - 1, day! + days));
  return [
    value.getUTCFullYear(),
    String(value.getUTCMonth() + 1).padStart(2, "0"),
    String(value.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

function mondayFor(dateKey: string): string {
  const [year, month, day] = dateKey.split("-").map(Number);
  const value = new Date(Date.UTC(year!, month! - 1, day!));
  const dayOfWeek = value.getUTCDay();
  const delta = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  return addDays(dateKey, delta);
}

function zonedLocalToIso(
  dateKey: string,
  time: string,
  timeZone: string,
): string {
  const [year, month, day] = dateKey.split("-").map(Number);
  const [hour, minute] = time.split(":").map(Number);
  const desiredAsUtc = Date.UTC(
    year!,
    month! - 1,
    day!,
    hour ?? 0,
    minute ?? 0,
    0,
  );
  let guess = desiredAsUtc;

  for (let iteration = 0; iteration < 3; iteration += 1) {
    const actual = partsInZone(new Date(guess), timeZone);
    const actualAsUtc = Date.UTC(
      actual.year,
      actual.month - 1,
      actual.day,
      actual.hour,
      actual.minute,
      actual.second,
    );
    guess += desiredAsUtc - actualAsUtc;
  }

  return new Date(guess).toISOString();
}

function agendaRange(dateKey: string, view: AgendaView, timeZone: string) {
  const startKey =
    view === "week" || view === "list" ? mondayFor(dateKey) : dateKey;
  const endKey = addDays(
    startKey,
    view === "week" || view === "list" ? 7 : 1,
  );
  return {
    from: zonedLocalToIso(startKey, "00:00", timeZone),
    to: zonedLocalToIso(endKey, "00:00", timeZone),
    startKey,
    endKey,
  };
}

export function displayDate(dateKey: string): string {
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Intl.DateTimeFormat("es-AR", {
    timeZone: "UTC",
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(new Date(Date.UTC(year!, month! - 1, day!)));
}

function timeInZone(value: string, timeZone: string): string {
  return new Intl.DateTimeFormat("es-AR", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(value));
}

function statusLabel(status: SalonAppointmentStatus | string): string {
  switch (status) {
    case "confirmed":
      return "Confirmado";
    case "pending":
      return "Pendiente";
    case "arrived":
      return "Llegó";
    case "in_service":
      return "En servicio";
    case "completed":
      return "Completado";
    case "cancelled":
      return "Cancelado";
    case "no_show":
      return "No asistió";
    default:
      return status;
  }
}

function paymentLabel(status: string): string {
  switch (status) {
    case "paid":
      return "Pagado";
    case "partial":
      return "Pago parcial";
    case "unpaid":
      return "Pendiente";
    case "waived":
      return "Cortesía";
    default:
      return status;
  }
}

function errorMessage(error: unknown): string {
  if (error instanceof PlatformApiError) return error.message;
  return "No se pudo completar la operación.";
}

function conflictAlternatives(error: unknown): readonly SalonAvailabilitySlot[] {
  if (!(error instanceof PlatformApiError)) return [];
  if (error.code !== "scheduling_booking_conflict") return [];
  const alternatives = error.details?.alternatives;
  if (!Array.isArray(alternatives)) return [];
  return alternatives.filter(
    (value): value is SalonAvailabilitySlot =>
      typeof value === "object" &&
      value !== null &&
      typeof (value as { startsAt?: unknown }).startsAt === "string" &&
      typeof (value as { endsAt?: unknown }).endsAt === "string",
  );
}

function localMinutes(value: string, timeZone: string): number {
  const parts = partsInZone(new Date(value), timeZone);
  return parts.hour * 60 + parts.minute;
}

function statusModifier(
  status: string,
  prefix: "cd-agenda-block" | "cd-agenda-tl-block",
): string {
  switch (status) {
    case "confirmed":
    case "arrived":
      return `${prefix}--confirmed`;
    case "in_service":
      return `${prefix}--ai`;
    case "pending":
      return `${prefix}--pending`;
    case "completed":
      return `${prefix}--completed`;
    case "cancelled":
      return `${prefix}--cancelled`;
    case "no_show":
      return `${prefix}--attention`;
    default:
      return `${prefix}--neutral`;
  }
}

function appointmentTopPx(
  item: SalonAgendaAppointment,
  timeZone: string,
): number {
  const minutes = localMinutes(item.startsAt, timeZone) - HOUR_START * 60;
  return Math.max(
    0,
    Math.round(
      minutes * (AGENDA_SLOT_HEIGHT_PX / AGENDA_INTERVAL_MINUTES),
    ),
  );
}

function appointmentHeightPx(item: SalonAgendaAppointment): number {
  return Math.max(
    34,
    Math.round(
      item.durationMinutes *
        (AGENDA_SLOT_HEIGHT_PX / AGENDA_INTERVAL_MINUTES),
    ),
  );
}

function appointmentLeftPx(
  item: SalonAgendaAppointment,
  timeZone: string,
): number {
  const minutes = localMinutes(item.startsAt, timeZone) - HOUR_START * 60;
  return Math.max(
    0,
    Math.round(
      minutes * (TIMELINE_SLOT_WIDTH_PX / AGENDA_INTERVAL_MINUTES),
    ),
  );
}

function appointmentWidthPx(item: SalonAgendaAppointment): number {
  return Math.max(
    56,
    Math.round(
      item.durationMinutes *
        (TIMELINE_SLOT_WIDTH_PX / AGENDA_INTERVAL_MINUTES),
    ),
  );
}

function blockTopPx(block: SalonAgendaBlock, timeZone: string): number {
  const minutes = localMinutes(block.startsAt, timeZone) - HOUR_START * 60;
  return Math.max(
    0,
    Math.round(
      minutes * (AGENDA_SLOT_HEIGHT_PX / AGENDA_INTERVAL_MINUTES),
    ),
  );
}

function blockHeightPx(block: SalonAgendaBlock): number {
  const minutes = Math.max(
    AGENDA_INTERVAL_MINUTES,
    Math.round((Date.parse(block.endsAt) - Date.parse(block.startsAt)) / 60_000),
  );
  return Math.max(
    28,
    Math.round(
      minutes * (AGENDA_SLOT_HEIGHT_PX / AGENDA_INTERVAL_MINUTES),
    ),
  );
}

function appointmentUsesResource(
  item: SalonAgendaAppointment,
  resourceId: string,
): boolean {
  return (
    item.resourceId === resourceId ||
    item.resourceAssignments.some(
      (assignment) => assignment.resourceId === resourceId,
    )
  );
}

function AppointmentButton({
  item,
  timeZone,
  mode,
  onOpen,
}: {
  item: SalonAgendaAppointment;
  timeZone: string;
  mode: "vertical" | "horizontal";
  onOpen: () => void;
}) {
  if (mode === "horizontal") {
    return (
      <button
        type="button"
        className={`cd-agenda-tl-block ${statusModifier(
          item.status,
          "cd-agenda-tl-block",
        )}`}
        style={{
          left: appointmentLeftPx(item, timeZone),
          width: appointmentWidthPx(item),
        }}
        onClick={onOpen}
        title={`${item.customerName} · ${timeInZone(
          item.startsAt,
          timeZone,
        )} · ${statusLabel(item.status)}`}
      >
        <span className="cd-agenda-tl-block__time">
          {timeInZone(item.startsAt, timeZone)}
        </span>
        <span className="cd-agenda-tl-block__name">{item.customerName}</span>
        <span className="cd-agenda-tl-block__svc">{item.serviceName}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      className={`cd-agenda-block ${statusModifier(
        item.status,
        "cd-agenda-block",
      )}${item.durationMinutes <= 60 ? " cd-agenda-block--compact" : ""}`}
      style={{
        top: appointmentTopPx(item, timeZone),
        height: appointmentHeightPx(item),
      }}
      onClick={onOpen}
      title={`${item.customerName} · ${timeInZone(
        item.startsAt,
        timeZone,
      )} · ${statusLabel(item.status)}`}
    >
      <div className="cd-agenda-block__row">
        <span className="cd-agenda-block__time">
          {timeInZone(item.startsAt, timeZone)}
        </span>
      </div>
      <span className="cd-agenda-block__name">{item.customerName}</span>
      <span className="cd-agenda-block__service">{item.serviceName}</span>
      <span className="cd-agenda-block__staff">
        {item.professionalName ?? item.locationName ?? "Sin asignar"}
      </span>
      <div className="cd-agenda-block__meta">
        <span className="cd-agenda-block__badge">
          {statusLabel(item.status)}
        </span>
      </div>
    </button>
  );
}

function ConflictAlternatives({
  alternatives,
  timeZone,
  onChoose,
}: {
  alternatives: readonly SalonAvailabilitySlot[];
  timeZone: string;
  onChoose: (slot: SalonAvailabilitySlot) => void;
}) {
  if (alternatives.length === 0) return null;
  return (
    <div className="cd-agenda-conflict-options" role="status">
      <strong>Ese horario ya no está disponible.</strong>
      <p>Elegí una alternativa calculada por Scheduling:</p>
      <div className="cd-agenda-chips">
        {alternatives.slice(0, 5).map((slot) => (
          <button
            key={slot.startsAt}
            type="button"
            className="cd-agenda-chip"
            onClick={() => onChoose(slot)}
          >
            {displayDate(dateKeyInZone(slot.startsAt, timeZone))} ·{" "}
            {timeInZone(slot.startsAt, timeZone)}
          </button>
        ))}
      </div>
    </div>
  );
}

function AppointmentDrawer({
  item,
  organizationId,
  api,
  depositPolicy,
  timeZone,
  busy,
  error,
  alternatives,
  basePath,
  navigate,
  onClose,
  onReschedule,
  onCancel,
  onTransition,
  onSaveNotes,
}: {
  item: SalonAgendaAppointment;
  organizationId: string;
  api: PlatformApiClient;
  depositPolicy: SalonAgenda["depositPolicy"];
  timeZone: string;
  busy: boolean;
  error: string | null;
  alternatives: readonly SalonAvailabilitySlot[];
  basePath: string;
  navigate: (path: string) => void;
  onClose: () => void;
  onReschedule: (startsAt: string) => Promise<void>;
  onCancel: () => Promise<void>;
  onTransition: (
    status: Exclude<SalonAppointmentStatus, "pending" | "cancelled">,
  ) => Promise<void>;
  onSaveNotes: (
    notes: string,
    expectedRevision: number,
  ) => Promise<{ notes: string; revision: number; updatedAt: string }>;
}) {
  const [date, setDate] = useState(
    dateKeyInZone(item.startsAt, timeZone),
  );
  const [time, setTime] = useState(timeInZone(item.startsAt, timeZone));
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [notes, setNotes] = useState(item.notes);
  const [notesRevision, setNotesRevision] = useState(item.notesRevision);
  const [notesSavedAt, setNotesSavedAt] = useState(item.notesUpdatedAt);
  const [deposit, setDeposit] = useState<SalonAppointmentDeposit | null>(
    item.deposit,
  );
  const [depositBusy, setDepositBusy] = useState(false);
  const [depositError, setDepositError] = useState<string | null>(null);

  async function refreshDeposit() {
    setDepositBusy(true);
    setDepositError(null);
    try {
      setDeposit(await api.salonAppointmentDeposit(organizationId, item.id));
    } catch (cause) {
      setDepositError(errorMessage(cause));
    } finally {
      setDepositBusy(false);
    }
  }

  async function createDepositCheckout() {
    setDepositBusy(true);
    setDepositError(null);
    try {
      const result = await api.createSalonAppointmentDepositCheckout(
        organizationId,
        item.id,
      );
      setDeposit(result.deposit);
      if (result.checkout.checkoutUrl) {
        window.open(
          result.checkout.checkoutUrl,
          "_blank",
          "noopener,noreferrer",
        );
      }
    } catch (cause) {
      setDepositError(errorMessage(cause));
    } finally {
      setDepositBusy(false);
    }
  }

  const actionButtons = (() => {
    switch (item.status) {
      case "pending":
        return [
          { status: "confirmed" as const, label: "Confirmar" },
          { status: "no_show" as const, label: "No asistió" },
        ];
      case "confirmed":
        return [
          { status: "arrived" as const, label: "Marcar llegada" },
          { status: "no_show" as const, label: "No asistió" },
        ];
      case "arrived":
        return [{ status: "in_service" as const, label: "Iniciar servicio" }];
      case "in_service":
        return [{ status: "completed" as const, label: "Completar" }];
      default:
        return [];
    }
  })();

  const canReschedule = !["completed", "cancelled", "no_show"].includes(
    item.status,
  );
  const canCancel = !["completed", "cancelled", "no_show"].includes(
    item.status,
  );

  return (
    <div className="cd-agenda-drawer-layer">
      <button
        type="button"
        className="cd-agenda-drawer-backdrop"
        aria-label="Cerrar detalle del turno"
        onClick={onClose}
      />
      <aside
        className="cd-agenda-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Detalle del turno"
      >
        <header>
          <div>
            <p className="cd-page-header__eyebrow">Turno</p>
            <h2>{item.customerName}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </header>

        <dl>
          <div>
            <dt>Servicio</dt>
            <dd>{item.serviceName}</dd>
          </div>
          <div>
            <dt>Estado</dt>
            <dd>{statusLabel(item.status)}</dd>
          </div>
          <div>
            <dt>Horario</dt>
            <dd>
              {displayDate(dateKeyInZone(item.startsAt, timeZone))} ·{" "}
              {timeInZone(item.startsAt, timeZone)}
            </dd>
          </div>
          <div>
            <dt>Duración</dt>
            <dd>{item.durationMinutes} min</dd>
          </div>
          <div>
            <dt>Local</dt>
            <dd>{item.locationName ?? "Sin local"}</dd>
          </div>
          <div>
            <dt>Origen</dt>
            <dd>{item.origin ?? "Sin origen registrado"}</dd>
          </div>
        </dl>

        <section className="cd-agenda-command-section">
          <h3>Recursos</h3>
          {item.resourceAssignments.length === 0 ? (
            <p>Sin recursos asignados.</p>
          ) : (
            <ul className="cd-agenda-resource-list">
              {item.resourceAssignments.map((assignment) => (
                <li
                  key={`${assignment.requirementKey}:${assignment.resourceId}`}
                >
                  <strong>{assignment.resourceName}</strong>
                  <span>
                    {assignment.resourceType} · {assignment.requirementKey}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {depositPolicy.mode !== "none" && (
          <section className="cd-agenda-command-section">
            <h3>Seña / depósito</h3>
            {deposit ? (
              <>
                <p>
                  {deposit.status === "approved"
                    ? "Aprobado"
                    : deposit.status === "pending" || deposit.status === "created"
                      ? "Pendiente de confirmación"
                      : deposit.status === "partially_refunded"
                        ? "Reembolso parcial"
                        : deposit.status === "refunded"
                          ? "Reembolsado"
                          : deposit.status === "rejected"
                            ? "Rechazado"
                            : "Cancelado"} · {deposit.currency}{" "}
                  {(deposit.amountMinor / 100).toLocaleString("es-AR")}
                </p>
                <p className="cd-field__hint">
                  Estado canónico reconciliado desde Payments. La redirección de
                  Mercado Pago nunca aprueba el depósito por sí sola.
                </p>
              </>
            ) : (
              <p>
                La política exige una seña de{" "}
                {depositPolicy.mode === "percentage"
                  ? `${depositPolicy.value}%`
                  : `ARS ${(depositPolicy.value / 100).toLocaleString("es-AR")}`}.
              </p>
            )}
            {depositError && (
              <div className="inline-alert" role="alert">{depositError}</div>
            )}
            <div className="cd-agenda-command-actions">
              {!deposit && (
                <button
                  type="button"
                  className="cd-button cd-button--primary"
                  disabled={depositBusy}
                  onClick={() => void createDepositCheckout()}
                >
                  {depositBusy ? "Creando checkout…" : "Cobrar seña con Mercado Pago"}
                </button>
              )}
              <button
                type="button"
                className="cd-button"
                disabled={depositBusy}
                onClick={() => void refreshDeposit()}
              >
                {depositBusy ? "Actualizando…" : "Actualizar depósito"}
              </button>
            </div>
          </section>
        )}

        {item.sale && (
          <section className="cd-agenda-command-section">
            <h3>Cobro</h3>
            <p>
              {paymentLabel(item.sale.paymentStatus)} · {item.sale.currency}{" "}
              {(item.sale.collectedAmountMinor / 100).toLocaleString("es-AR")}
            </p>
          </section>
        )}

        <section className="cd-agenda-command-section">
          <h3>Notas del turno</h3>
          <label>
            <span className="sr-only">Notas del turno</span>
            <textarea
              value={notes}
              maxLength={4000}
              rows={4}
              placeholder="Preferencias o contexto operativo de este turno…"
              onChange={(event) => setNotes(event.target.value)}
            />
          </label>
          <div className="cd-agenda-command-actions">
            <button
              type="button"
              className="cd-button"
              disabled={busy || notes === item.notes && notesRevision === item.notesRevision}
              onClick={() =>
                void onSaveNotes(notes, notesRevision).then((saved) => {
                  setNotes(saved.notes);
                  setNotesRevision(saved.revision);
                  setNotesSavedAt(saved.updatedAt);
                })
              }
            >
              {busy ? "Guardando…" : "Guardar notas"}
            </button>
            {notesSavedAt && (
              <span className="cd-agenda-note-meta">
                Actualizadas{" "}
                {displayDate(dateKeyInZone(notesSavedAt, timeZone))} ·{" "}
                {timeInZone(notesSavedAt, timeZone)}
              </span>
            )}
          </div>
        </section>

        <div className="cd-agenda-detail-actions">
          {item.customerId && (
            <button
              type="button"
              className="cd-button"
              onClick={() =>
                navigate(`${basePath}/customers/${item.customerId}`)
              }
            >
              Abrir cliente
            </button>
          )}
          {item.conversationId && (
            <button
              type="button"
              className="cd-button"
              onClick={() =>
                navigate(
                  `${basePath}/conversations/${encodeURIComponent(
                    item.conversationId!,
                  )}`,
                )
              }
            >
              Abrir conversación
            </button>
          )}
          <button
            type="button"
            className="cd-button"
            onClick={() => navigate(`${basePath}/sales`)}
          >
            {item.sale ? "Abrir Ventas" : "Cobrar / crear venta"}
          </button>
        </div>

        {error && (
          <div className="inline-alert" role="alert">
            {error}
          </div>
        )}

        <ConflictAlternatives
          alternatives={alternatives}
          timeZone={timeZone}
          onChoose={(slot) => {
            setDate(dateKeyInZone(slot.startsAt, timeZone));
            setTime(timeInZone(slot.startsAt, timeZone));
          }}
        />

        {actionButtons.length > 0 && (
          <section className="cd-agenda-command-section">
            <h3>Estado del turno</h3>
            <div className="cd-agenda-command-actions">
              {actionButtons.map((action) => (
                <button
                  key={action.status}
                  type="button"
                  className="cd-button cd-button--primary"
                  disabled={busy}
                  onClick={() => void onTransition(action.status)}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </section>
        )}

        {canReschedule && (
          <section className="cd-agenda-command-section">
            <h3>Reprogramar</h3>
            <div className="cd-agenda-command-grid">
              <label>
                Fecha
                <input
                  type="date"
                  value={date}
                  onChange={(event) => setDate(event.target.value)}
                />
              </label>
              <label>
                Hora
                <input
                  type="time"
                  value={time}
                  onChange={(event) => setTime(event.target.value)}
                />
              </label>
            </div>
            <button
              type="button"
              className="cd-button cd-button--primary"
              disabled={busy || !date || !time}
              onClick={() =>
                onReschedule(zonedLocalToIso(date, time, timeZone))
              }
            >
              {busy ? "Guardando…" : "Guardar nuevo horario"}
            </button>
          </section>
        )}

        {canCancel && (
          <section className="cd-agenda-command-section cd-agenda-command-section--danger">
            <h3>Cancelar turno</h3>
            {!cancelConfirm ? (
              <button
                type="button"
                className="cd-button"
                onClick={() => setCancelConfirm(true)}
              >
                Cancelar turno
              </button>
            ) : (
              <div className="cd-agenda-cancel-confirm">
                <p>
                  El turno se cancela en Scheduling. Los conectores externos no
                  se convierten en la fuente de verdad.
                </p>
                <div>
                  <button
                    type="button"
                    className="cd-button"
                    onClick={() => setCancelConfirm(false)}
                  >
                    Volver
                  </button>
                  <button
                    type="button"
                    className="cd-button cd-button--danger"
                    disabled={busy}
                    onClick={onCancel}
                  >
                    Confirmar cancelación
                  </button>
                </div>
              </div>
            )}
          </section>
        )}
      </aside>
    </div>
  );
}

function NewAppointmentDrawer({
  organizationId,
  overview,
  agenda,
  api,
  timeZone,
  initialDate,
  initialTime,
  initialResourceId,
  initialCustomerId,
  busy,
  error,
  alternatives,
  onClose,
  onCreate,
}: {
  organizationId: string;
  overview: SalonOverview;
  agenda: SalonAgenda;
  api: PlatformApiClient;
  timeZone: string;
  initialDate: string;
  initialTime: string | null;
  initialResourceId: string | null;
  initialCustomerId: string | null;
  busy: boolean;
  error: string | null;
  alternatives: readonly SalonAvailabilitySlot[];
  onClose: () => void;
  onCreate: (input: {
    serviceId: string;
    customerId: string | null;
    locationId: string | null;
    resourceId: string | null;
    preferredResourceIds: readonly string[];
    startsAt: string;
  }) => Promise<void>;
}) {
  const activeServices = overview.services.filter(
    (service) => service.active,
  );
  const activeLocations = agenda.locations.filter(
    (location) => location.active,
  );
  const activeResources = agenda.resources.filter(
    (resource) => resource.active,
  );

  const [customerId, setCustomerId] = useState(initialCustomerId ?? "");
  const [serviceId, setServiceId] = useState(activeServices[0]?.id ?? "");
  const [date, setDate] = useState(initialDate);
  const [locationId, setLocationId] = useState(
    activeLocations[0]?.id ?? "",
  );
  const [resourceId, setResourceId] = useState(initialResourceId ?? "");
  const [slots, setSlots] = useState<readonly SalonAvailabilitySlot[]>([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(
    null,
  );
  const [selectedStartsAt, setSelectedStartsAt] = useState<string | null>(
    initialTime
      ? zonedLocalToIso(initialDate, initialTime, timeZone)
      : null,
  );

  useEffect(() => {
    let current = true;
    if (!serviceId || !date) {
      setSlots([]);
      return () => {
        current = false;
      };
    }

    setAvailabilityLoading(true);
    setAvailabilityError(null);
    api
      .salonSchedulingAvailability(organizationId, {
        serviceId,
        startsAfter: zonedLocalToIso(date, "00:00", timeZone),
        endsBefore: zonedLocalToIso(addDays(date, 1), "00:00", timeZone),
        timeZone,
        locationId: locationId || null,
        resourceId: resourceId || null,
        preferredResourceIds: resourceId ? [resourceId] : [],
      })
      .then((next) => {
        if (!current) return;
        setSlots(next);
        if (
          selectedStartsAt &&
          !next.some((slot) => slot.startsAt === selectedStartsAt)
        ) {
          setSelectedStartsAt(null);
        }
      })
      .catch((cause) => {
        if (!current) return;
        setSlots([]);
        setAvailabilityError(errorMessage(cause));
      })
      .finally(() => {
        if (current) setAvailabilityLoading(false);
      });

    return () => {
      current = false;
    };
  }, [
    api,
    organizationId,
    serviceId,
    date,
    locationId,
    resourceId,
    timeZone,
  ]);

  useEffect(() => {
    if (!initialTime || slots.length === 0) return;
    const preferred = zonedLocalToIso(initialDate, initialTime, timeZone);
    if (slots.some((slot) => slot.startsAt === preferred)) {
      setSelectedStartsAt(preferred);
    }
  }, [initialDate, initialTime, slots, timeZone]);

  const selectedSlot =
    slots.find((slot) => slot.startsAt === selectedStartsAt) ?? null;

  return (
    <div className="cd-agenda-drawer-layer">
      <button
        type="button"
        className="cd-agenda-drawer-backdrop"
        aria-label="Cerrar nuevo turno"
        onClick={onClose}
      />
      <aside
        className="cd-agenda-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Nuevo turno"
      >
        <header>
          <div>
            <p className="cd-page-header__eyebrow">Agenda</p>
            <h2>Nuevo turno</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </header>

        <div className="cd-agenda-form">
          <label>
            Cliente
            <select
              value={customerId}
              onChange={(event) => setCustomerId(event.target.value)}
            >
              <option value="">Walk-in / sin cliente vinculado</option>
              {overview.customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.displayName}
                </option>
              ))}
            </select>
          </label>

          <label>
            Servicio
            <select
              value={serviceId}
              onChange={(event) => {
                setServiceId(event.target.value);
                setSelectedStartsAt(null);
              }}
            >
              {activeServices.length === 0 && (
                <option value="">Sin servicios activos</option>
              )}
              {activeServices.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name} · {service.durationMinutes} min
                </option>
              ))}
            </select>
          </label>

          <label>
            Local
            <select
              value={locationId}
              onChange={(event) => {
                setLocationId(event.target.value);
                setSelectedStartsAt(null);
              }}
            >
              <option value="">Cualquier local elegible</option>
              {activeLocations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Profesional / recurso preferido
            <select
              value={resourceId}
              onChange={(event) => {
                setResourceId(event.target.value);
                setSelectedStartsAt(null);
              }}
            >
              <option value="">Cualquiera disponible</option>
              {activeResources.map((resource) => (
                <option key={resource.id} value={resource.id}>
                  {resource.displayName ?? resource.name} ·{" "}
                  {resource.resourceType}
                </option>
              ))}
            </select>
          </label>

          <label>
            Fecha
            <input
              type="date"
              value={date}
              onChange={(event) => {
                setDate(event.target.value);
                setSelectedStartsAt(null);
              }}
            />
          </label>

          <section className="cd-agenda-command-section">
            <h3>Disponibilidad canónica</h3>
            {availabilityLoading && <p>Calculando disponibilidad…</p>}
            {availabilityError && (
              <div className="inline-alert" role="alert">
                {availabilityError}
              </div>
            )}
            {!availabilityLoading &&
              !availabilityError &&
              slots.length === 0 && (
                <p>No hay horarios disponibles para esta combinación.</p>
              )}
            <div className="cd-agenda-chips">
              {slots.map((slot) => (
                <button
                  key={slot.startsAt}
                  type="button"
                  className={
                    selectedStartsAt === slot.startsAt
                      ? "cd-agenda-chip cd-agenda-chip--active"
                      : "cd-agenda-chip"
                  }
                  onClick={() => setSelectedStartsAt(slot.startsAt)}
                >
                  {timeInZone(slot.startsAt, timeZone)}
                </button>
              ))}
            </div>
          </section>

          <ConflictAlternatives
            alternatives={alternatives}
            timeZone={timeZone}
            onChoose={(slot) => {
              setDate(dateKeyInZone(slot.startsAt, timeZone));
              setSelectedStartsAt(slot.startsAt);
              if (slot.locationId) setLocationId(slot.locationId);
              if (slot.resourceId) setResourceId(slot.resourceId);
            }}
          />

          {error && (
            <div className="inline-alert" role="alert">
              {error}
            </div>
          )}

          <button
            type="button"
            className="cd-button cd-button--primary"
            disabled={!serviceId || !selectedSlot || busy}
            onClick={() =>
              selectedSlot &&
              onCreate({
                serviceId,
                customerId: customerId || null,
                locationId:
                  selectedSlot.locationId ?? (locationId || null),
                resourceId: resourceId || selectedSlot.resourceId || null,
                preferredResourceIds: resourceId ? [resourceId] : [],
                startsAt: selectedSlot.startsAt,
              })
            }
          >
            {busy ? "Creando…" : "Crear turno"}
          </button>
        </div>
      </aside>
    </div>
  );
}

function NewBlockDrawer({
  organizationId,
  agenda,
  api,
  timeZone,
  initialDate,
  initialTime,
  initialResourceId,
  busy,
  error,
  onClose,
  onCreated,
}: {
  organizationId: string;
  agenda: SalonAgenda;
  api: PlatformApiClient;
  timeZone: string;
  initialDate: string;
  initialTime: string | null;
  initialResourceId: string | null;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const activeLocations = agenda.locations.filter(
    (location) => location.active,
  );
  const [locationId, setLocationId] = useState(
    activeLocations[0]?.id ?? "",
  );
  const [resourceIds, setResourceIds] = useState<readonly string[]>(
    initialResourceId ? [initialResourceId] : [],
  );
  const [date, setDate] = useState(initialDate);
  const [startTime, setStartTime] = useState(initialTime ?? "12:00");
  const [endTime, setEndTime] = useState(() => {
    const [hour, minute] = (initialTime ?? "12:00").split(":").map(Number);
    const end = Math.min(23 * 60 + 59, (hour ?? 12) * 60 + (minute ?? 0) + 60);
    return `${String(Math.floor(end / 60)).padStart(2, "0")}:${String(
      end % 60,
    ).padStart(2, "0")}`;
  });
  const [reason, setReason] = useState("");

  return (
    <div className="cd-agenda-drawer-layer">
      <button
        type="button"
        className="cd-agenda-drawer-backdrop"
        aria-label="Cerrar bloqueo"
        onClick={onClose}
      />
      <aside
        className="cd-agenda-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Nuevo bloqueo"
      >
        <header>
          <div>
            <p className="cd-page-header__eyebrow">Agenda</p>
            <h2>Nuevo bloqueo</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </header>

        <div className="cd-agenda-form">
          <label>
            Local
            <select
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
            >
              <option value="">Todo el negocio</option>
              {activeLocations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>
          <fieldset className="cd-agenda-block-resources">
            <legend>Recursos</legend>
            <p className="cd-agenda-form__hint">
              Sin selección bloquea todo el alcance. Podés elegir uno o varios
              recursos específicos.
            </p>
            {agenda.resources
              .filter((resource) => resource.active)
              .map((resource) => (
                <label key={resource.id} className="cd-agenda-block-resource">
                  <input
                    type="checkbox"
                    checked={resourceIds.includes(resource.id)}
                    onChange={(event) =>
                      setResourceIds((current) =>
                        event.target.checked
                          ? current.includes(resource.id)
                            ? current
                            : [...current, resource.id]
                          : current.filter((id) => id !== resource.id),
                      )
                    }
                  />
                  <span>
                    {resource.displayName ?? resource.name} ·{" "}
                    {resource.resourceType}
                  </span>
                </label>
              ))}
          </fieldset>
          <label>
            Fecha
            <input
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
          </label>
          <div className="cd-agenda-command-grid">
            <label>
              Desde
              <input
                type="time"
                value={startTime}
                onChange={(event) => setStartTime(event.target.value)}
              />
            </label>
            <label>
              Hasta
              <input
                type="time"
                value={endTime}
                onChange={(event) => setEndTime(event.target.value)}
              />
            </label>
          </div>
          <label>
            Motivo
            <input
              value={reason}
              placeholder="Almuerzo, capacitación, ausencia…"
              onChange={(event) => setReason(event.target.value)}
            />
          </label>

          {error && (
            <div className="inline-alert" role="alert">
              {error}
            </div>
          )}

          <button
            type="button"
            className="cd-button cd-button--primary"
            disabled={busy || !date || startTime >= endTime}
            onClick={() =>
              void (async () => {
                await api.createSalonAgendaBlocks(organizationId, {
                  locationId: locationId || null,
                  resourceIds,
                  startsAt: zonedLocalToIso(date, startTime, timeZone),
                  endsAt: zonedLocalToIso(date, endTime, timeZone),
                  reason: reason.trim() || null,
                  visibleLabel: reason.trim() || "Bloqueo",
                });
                await onCreated();
              })()
            }
          >
            {busy ? "Guardando…" : "Crear bloqueo"}
          </button>
        </div>
      </aside>
    </div>
  );
}

export function SalonsAgenda({
  organizationId,
  overview,
  api,
  navigate,
  basePath,
  onOverviewRefresh,
}: {
  organizationId: string;
  overview: SalonOverview;
  api: PlatformApiClient;
  navigate: (path: string) => void;
  basePath: string;
  onOverviewRefresh: () => Promise<void>;
}) {
  const [view, setView] = useState<AgendaView>("day");
  const [selectedDate, setSelectedDate] = useState(() =>
    dateKeyInZone(new Date(), overview.timeZone),
  );
  const [agenda, setAgenda] = useState<SalonAgenda | null>(null);
  const [loading, setLoading] = useState(true);
  const [operationBusy, setOperationBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alternatives, setAlternatives] = useState<
    readonly SalonAvailabilitySlot[]
  >([]);
  const [selectedAppointment, setSelectedAppointment] =
    useState<SalonAgendaAppointment | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [resourceFilter, setResourceFilter] = useState("all");
  const [listSort, setListSort] = useState<AgendaListSort>("date_asc");
  const [prefillTime, setPrefillTime] = useState<string | null>(null);
  const [prefillResourceId, setPrefillResourceId] = useState<string | null>(
    null,
  );

  const routeQuery = new URLSearchParams(window.location.search);
  const requestedCustomerId = routeQuery.get("customerId");
  const requestedAppointmentId = routeQuery.get("appointmentId");
  const requestedStartsAt = routeQuery.get("startsAt");
  const requestedAppointmentDate =
    requestedStartsAt && Number.isFinite(Date.parse(requestedStartsAt))
      ? dateKeyInZone(requestedStartsAt, overview.timeZone)
      : null;
  const initialCustomerId =
    requestedCustomerId &&
    overview.customers.some((customer) => customer.id === requestedCustomerId)
      ? requestedCustomerId
      : null;

  const range = useMemo(
    () => agendaRange(selectedDate, view, overview.timeZone),
    [selectedDate, view, overview.timeZone],
  );

  async function loadAgenda() {
    setLoading(true);
    setError(null);
    try {
      setAgenda(await api.salonAgenda(organizationId, range.from, range.to));
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(null);
    api
      .salonAgenda(organizationId, range.from, range.to)
      .then((next) => {
        if (current) setAgenda(next);
      })
      .catch((cause) => {
        if (current) setError(errorMessage(cause));
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [api, organizationId, range.from, range.to]);

  useEffect(() => {
    if (
      requestedAppointmentDate &&
      requestedAppointmentDate !== selectedDate
    ) {
      setSelectedDate(requestedAppointmentDate);
      return;
    }

    if (!agenda) return;

    if (requestedAppointmentId) {
      const requestedAppointment = agenda.appointments.find(
        (item) => item.id === requestedAppointmentId,
      );
      if (requestedAppointment) {
        setSelectedAppointment(requestedAppointment);
      }
      return;
    }

    if (initialCustomerId) {
      setCreateOpen(true);
    }
  }, [
    agenda,
    initialCustomerId,
    requestedAppointmentDate,
    requestedAppointmentId,
    selectedDate,
  ]);

  const appointments = useMemo(() => {
    const source = agenda?.appointments ?? [];
    return source.filter(
      (item) =>
        (statusFilter === "all" || item.status === statusFilter) &&
        (serviceFilter === "all" || item.serviceId === serviceFilter) &&
        (locationFilter === "all" || item.locationId === locationFilter) &&
        (resourceFilter === "all" ||
          appointmentUsesResource(item, resourceFilter)),
    );
  }, [
    agenda,
    statusFilter,
    serviceFilter,
    locationFilter,
    resourceFilter,
  ]);

  const listAppointments = useMemo(() => {
    const next = [...appointments];
    next.sort((left, right) => {
      if (listSort === "date_desc") {
        return (
          Date.parse(right.startsAt) - Date.parse(left.startsAt) ||
          left.id.localeCompare(right.id)
        );
      }
      if (listSort === "customer_asc") {
        return (
          left.customerName.localeCompare(right.customerName, "es") ||
          Date.parse(left.startsAt) - Date.parse(right.startsAt)
        );
      }
      if (listSort === "service_asc") {
        return (
          left.serviceName.localeCompare(right.serviceName, "es") ||
          Date.parse(left.startsAt) - Date.parse(right.startsAt)
        );
      }
      if (listSort === "status_asc") {
        return (
          statusLabel(left.status).localeCompare(
            statusLabel(right.status),
            "es",
          ) ||
          Date.parse(left.startsAt) - Date.parse(right.startsAt)
        );
      }
      return (
        Date.parse(left.startsAt) - Date.parse(right.startsAt) ||
        left.id.localeCompare(right.id)
      );
    });
    return next;
  }, [appointments, listSort]);

  const selectedDayAppointments = appointments.filter(
    (item) =>
      dateKeyInZone(item.startsAt, overview.timeZone) === selectedDate,
  );

  const activeResources = useMemo(
    () => agenda?.resources.filter((resource) => resource.active) ?? [],
    [agenda],
  );
  const professionalResources = activeResources.filter(
    (resource) => resource.resourceType === "professional",
  );
  const columnResources: readonly SalonAgendaResource[] =
    resourceFilter !== "all"
      ? activeResources.filter((resource) => resource.id === resourceFilter)
      : professionalResources.length > 0
        ? professionalResources
        : activeResources;

  const dayColumns: readonly (SalonAgendaResource | null)[] =
    columnResources.length > 0 ? columnResources : [null];

  const visibleBlocks = (agenda?.blocks ?? []).filter(
    (block) =>
      dateKeyInZone(block.startsAt, overview.timeZone) === selectedDate &&
      (locationFilter === "all" || block.locationId === locationFilter),
  );

  const weekStart = mondayFor(selectedDate);
  const weekKeys = Array.from({ length: 7 }, (_, index) =>
    addDays(weekStart, index),
  );
  const slotCount = MINUTES_IN_DAY_WINDOW / AGENDA_INTERVAL_MINUTES;
  const slots = Array.from({ length: slotCount + 1 }, (_, index) => {
    const minutes = HOUR_START * 60 + index * AGENDA_INTERVAL_MINUTES;
    return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(
      minutes % 60,
    ).padStart(2, "0")}`;
  });
  const matrixHeight = slotCount * AGENDA_SLOT_HEIGHT_PX;
  const timelineTrackWidth = slotCount * TIMELINE_SLOT_WIDTH_PX;
  const currentDateKey = dateKeyInZone(new Date(), overview.timeZone);
  const nowParts = partsInZone(new Date(), overview.timeZone);
  const nowMinutes = nowParts.hour * 60 + nowParts.minute;
  const nowTop =
    selectedDate === currentDateKey &&
    nowMinutes >= HOUR_START * 60 &&
    nowMinutes <= HOUR_END * 60
      ? Math.round(
          (nowMinutes - HOUR_START * 60) *
            (AGENDA_SLOT_HEIGHT_PX / AGENDA_INTERVAL_MINUTES),
        )
      : null;

  function moveDate(delta: number) {
    const step = view === "week" || view === "list" ? 7 : 1;
    setSelectedDate((current) => addDays(current, delta * step));
  }

  async function mutate(operation: () => Promise<unknown>) {
    setOperationBusy(true);
    setError(null);
    setAlternatives([]);
    try {
      await operation();
      await Promise.all([loadAgenda(), onOverviewRefresh()]);
    } catch (cause) {
      setAlternatives(conflictAlternatives(cause));
      setError(errorMessage(cause));
      throw cause;
    } finally {
      setOperationBusy(false);
    }
  }

  async function reschedule(
    item: SalonAgendaAppointment,
    startsAt: string,
  ) {
    try {
      await mutate(() =>
        api.rescheduleSalonAgendaAppointment(
          organizationId,
          item.id,
          {
            startsAt,
            timeZone: overview.timeZone,
            preferredResourceIds: item.resourceAssignments.map(
              (assignment) => assignment.resourceId,
            ),
          },
        ),
      );
      setSelectedAppointment(null);
      if (requestedAppointmentId) {
        navigate(`${basePath}/agenda`);
      }
    } catch {
      // Recovery details remain visible in the drawer.
    }
  }

  async function cancel(item: SalonAgendaAppointment) {
    try {
      await mutate(() =>
        api.cancelSalonAgendaAppointment(organizationId, item.id),
      );
      setSelectedAppointment(null);
      if (requestedAppointmentId) {
        navigate(`${basePath}/agenda`);
      }
    } catch {
      // Error remains visible in the drawer.
    }
  }

  async function transition(
    item: SalonAgendaAppointment,
    status: Exclude<SalonAppointmentStatus, "pending" | "cancelled">,
  ) {
    try {
      await mutate(() =>
        api.transitionSalonAgendaAppointment(
          organizationId,
          item.id,
          status,
        ),
      );
      setSelectedAppointment(null);
      if (requestedAppointmentId) {
        navigate(`${basePath}/agenda`);
      }
    } catch {
      // Error remains visible in the drawer.
    }
  }

  async function saveAppointmentNotes(
    item: SalonAgendaAppointment,
    notes: string,
    expectedRevision: number,
  ) {
    setOperationBusy(true);
    setError(null);
    try {
      const saved = await api.updateSalonAgendaAppointmentNotes(
        organizationId,
        item.id,
        { notes, expectedRevision },
      );
      setSelectedAppointment((current) =>
        current?.id === item.id
          ? {
              ...current,
              notes: saved.notes,
              notesRevision: saved.revision,
              notesUpdatedAt: saved.updatedAt,
            }
          : current,
      );
      await loadAgenda();
      return saved;
    } catch (cause) {
      setError(errorMessage(cause));
      throw cause;
    } finally {
      setOperationBusy(false);
    }
  }

  async function createAppointment(input: {
    serviceId: string;
    customerId: string | null;
    locationId: string | null;
    resourceId: string | null;
    preferredResourceIds: readonly string[];
    startsAt: string;
  }) {
    try {
      await mutate(() =>
        api.createSalonAgendaAppointment(organizationId, {
          ...input,
          timeZone: overview.timeZone,
        }),
      );
      setCreateOpen(false);
      setPrefillTime(null);
      setPrefillResourceId(null);
      if (initialCustomerId) {
        navigate(`${basePath}/agenda`);
      }
    } catch {
      // Recovery details remain visible in the drawer.
    }
  }

  function openCreate(time: string | null, resourceId: string | null) {
    setAlternatives([]);
    setError(null);
    setPrefillTime(time);
    setPrefillResourceId(resourceId);
    setCreateOpen(true);
  }

  return (
    <section
      className="cd-agenda-product cd-agenda-alt"
      aria-label="Agenda de Peluquerías"
    >
      <header className="cd-page-header">
        <div>
          <h1 className="cd-page-header__title">Agenda</h1>
          <p className="cd-page-header__description">
            Gestioná turnos, profesionales, disponibilidad y bloqueos desde la agenda.
          </p>
        </div>
      </header>

      <div className="cd-agenda-alt__shell">
        <header
          className="cd-agenda-module-toolbar cd-agenda-alt__toolbar-card"
          aria-label="Controles de agenda"
        >
          <div className="cd-agenda-alt__toolbar-main">
            <div
              className="cd-agenda-alt__date-group"
              aria-label="Navegación de fecha"
            >
              <button
                type="button"
                className="cd-agenda-nav-btn cd-agenda-nav-btn--active"
                onClick={() => setSelectedDate(currentDateKey)}
              >
                Hoy
              </button>
              <button
                type="button"
                className="cd-agenda-nav-btn cd-agenda-nav-btn--icon"
                aria-label="Fecha anterior"
                onClick={() => moveDate(-1)}
              >
                <SalonIcon name="chevron_left" />
              </button>
              <button
                type="button"
                className="cd-agenda-nav-btn cd-agenda-nav-btn--icon"
                aria-label="Fecha siguiente"
                onClick={() => moveDate(1)}
              >
                <SalonIcon name="chevron_right" />
              </button>
              <div className="cd-agenda-datepicker-wrap">
                <label className="cd-agenda-date-trigger">
                  <SalonIcon
                    name="calendar_today"
                    className="cd-agenda-date-trigger__icon"
                  />
                  <span className="sr-only">Elegir fecha</span>
                  <input
                    className="cd-agenda-native-date"
                    type="date"
                    value={selectedDate}
                    onChange={(event) => setSelectedDate(event.target.value)}
                  />
                  <SalonIcon
                    name="expand_more"
                    className="cd-agenda-date-trigger__caret"
                  />
                </label>
              </div>
            </div>

            <div
              className="cd-agenda-view-switch cd-agenda-alt__view-switch"
              aria-label="Vista de agenda"
            >
              {(["day", "week", "list", "timeline"] as const).map((key) => (
                <button
                  type="button"
                  key={key}
                  className={
                    view === key
                      ? "cd-agenda-view-btn cd-agenda-view-btn--active"
                      : "cd-agenda-view-btn"
                  }
                  aria-pressed={view === key}
                  onClick={() => setView(key)}
                >
                  {key === "day"
                    ? "Día"
                    : key === "week"
                      ? "Semana"
                      : key === "list"
                        ? "Lista"
                        : "Timeline"}
                </button>
              ))}
            </div>

            <div className="cd-agenda-alt__toolbar-filters">
              <div className="cd-agenda-filter-slot">
                <label className="cd-agenda-filter-control">
                  Local
                  <select
                    value={locationFilter}
                    onChange={(event) => setLocationFilter(event.target.value)}
                  >
                    <option value="all">Todos</option>
                    {(agenda?.locations ?? []).map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="cd-agenda-filter-slot">
                <label className="cd-agenda-filter-control">
                  Profesional / recurso
                  <select
                    value={resourceFilter}
                    onChange={(event) => setResourceFilter(event.target.value)}
                  >
                    <option value="all">Todos</option>
                    {activeResources.map((resource) => (
                      <option key={resource.id} value={resource.id}>
                        {resource.displayName ?? resource.name} ·{" "}
                        {resource.resourceType}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="cd-agenda-filter-slot">
                <label className="cd-agenda-filter-control">
                  Estado
                  <select
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                  >
                    <option value="all">Todos</option>
                    <option value="pending">Pendientes</option>
                    <option value="confirmed">Confirmados</option>
                    <option value="arrived">Llegó</option>
                    <option value="in_service">En servicio</option>
                    <option value="completed">Completados</option>
                    <option value="no_show">No asistió</option>
                    <option value="cancelled">Cancelados</option>
                  </select>
                </label>
              </div>
              <div className="cd-agenda-filter-slot">
                <label className="cd-agenda-filter-control">
                  Servicio
                  <select
                    value={serviceFilter}
                    onChange={(event) => setServiceFilter(event.target.value)}
                  >
                    <option value="all">Todos</option>
                    {overview.services.map((service: SalonService) => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            {view === "list" && (
              <div className="cd-agenda-filter-slot">
                <label className="cd-agenda-filter-control">
                  Orden
                  <select
                    value={listSort}
                    onChange={(event) =>
                      setListSort(event.target.value as AgendaListSort)
                    }
                  >
                    <option value="date_asc">Fecha · más próximo</option>
                    <option value="date_desc">Fecha · más lejano</option>
                    <option value="customer_asc">Cliente · A–Z</option>
                    <option value="service_asc">Servicio · A–Z</option>
                    <option value="status_asc">Estado · A–Z</option>
                  </select>
                </label>
              </div>
            )}

            <ActionToolbar className="cd-agenda-alt__new-wrap" label="Acciones de agenda">
              <ActionButton variant="primary" onClick={() => openCreate(null, null)}>
                <SalonIcon name="add" />
                Nuevo turno
              </ActionButton>
              <OverflowMenu
                label="Más acciones de agenda"
                actions={[
                  {
                    key: "new-customer",
                    label: "Nuevo cliente",
                    onSelect: () => navigate(`${basePath}/customers?create=1&source=agenda`),
                  },
                  {
                    key: "new-block",
                    label: "Nuevo bloqueo",
                    onSelect: () => setBlockOpen(true),
                  },
                ]}
              />
            </ActionToolbar>
          </div>
        </header>

        {error && (
          <div className="cd-agenda-banner cd-agenda-banner--warn" role="alert">
            <span>{error}</span>
            <ActionButton variant="secondary" onClick={() => void loadAgenda()}>Reintentar</ActionButton>
          </div>
        )}

        {agenda?.truncated && (
          <div className="cd-agenda-banner cd-agenda-banner--warn" role="status">
            Este rango supera el límite de 1.000 turnos. Acotá la fecha o los
            filtros para operar sobre el conjunto completo.
          </div>
        )}

        <section
          className="cd-agenda-alt__calendar-card"
          aria-label="Calendario principal"
        >
          {loading && (
            <div className="cd-agenda-loading">Cargando agenda…</div>
          )}

          {!loading && view === "day" && (
            <div className="cd-agenda-matrix-wrap">
              <div
                className="cd-agenda-matrix cd-agenda-matrix--wide"
                style={
                  {
                    "--cd-agenda-cols": dayColumns.length,
                  } as CSSProperties
                }
              >
                <div className="cd-agenda-matrix__header">
                  <div className="cd-agenda-matrix__corner" />
                  {dayColumns.map((resource, index) => (
                    <div
                      key={resource?.id ?? `unassigned-${index}`}
                      className="cd-agenda-matrix__staff-head"
                    >
                      {resource
                        ? resource.displayName ?? resource.name
                        : "Sin asignar"}
                    </div>
                  ))}
                </div>

                <div className="cd-agenda-matrix__scroll">
                  <div
                    className="cd-agenda-matrix__body"
                    style={{ minHeight: matrixHeight }}
                  >
                    <div
                      className="cd-agenda-matrix__grid-bg"
                      style={
                        {
                          "--cd-agenda-cols": dayColumns.length,
                        } as CSSProperties
                      }
                    >
                      <div className="cd-agenda-matrix__times">
                        {slots.slice(0, -1).map((label) => (
                          <div
                            key={label}
                            className="cd-agenda-matrix__time-slot"
                          >
                            {label}
                          </div>
                        ))}
                      </div>
                      <div className="cd-agenda-matrix__col-borders">
                        {dayColumns.map((resource, index) => (
                          <div key={resource?.id ?? `border-${index}`} />
                        ))}
                      </div>
                    </div>

                    <div
                      className="cd-agenda-matrix__row-dividers"
                      aria-hidden="true"
                    >
                      {slots.slice(0, -1).map((label) => (
                        <div
                          key={label}
                          className="cd-agenda-matrix__row-line"
                        />
                      ))}
                    </div>

                    {nowTop !== null && (
                      <div
                        className="cd-agenda-matrix__now-line"
                        style={{ top: nowTop }}
                        aria-hidden="true"
                      >
                        <span className="cd-agenda-matrix__now-dot" />
                      </div>
                    )}

                    <div
                      className="cd-agenda-matrix__overlay"
                      style={
                        {
                          "--cd-agenda-cols": dayColumns.length,
                        } as CSSProperties
                      }
                    >
                      {dayColumns.map((resource, columnIndex) => {
                        const items = selectedDayAppointments.filter((item) =>
                          resource
                            ? appointmentUsesResource(item, resource.id)
                            : item.resourceAssignments.length === 0 &&
                              item.resourceId === null,
                        );
                        const resourceBlocks = visibleBlocks.filter((block) =>
                          resource
                            ? block.resourceId === resource.id ||
                              block.resourceId === null
                            : block.resourceId === null,
                        );

                        return (
                          <div
                            key={resource?.id ?? `unassigned-${columnIndex}`}
                            className="cd-agenda-matrix__col"
                            data-resource={resource?.id ?? "unassigned"}
                          >
                            {slots.slice(0, -1).map((label, index) => (
                              <button
                                key={label}
                                type="button"
                                className="cd-agenda-ghost-slot"
                                style={{
                                  top: index * AGENDA_SLOT_HEIGHT_PX,
                                  height: AGENDA_SLOT_HEIGHT_PX,
                                }}
                                aria-label={`Crear turno ${label} ${
                                  resource
                                    ? resource.displayName ?? resource.name
                                    : "sin recurso"
                                }`}
                                onClick={() =>
                                  openCreate(label, resource?.id ?? null)
                                }
                              >
                                <span className="cd-agenda-ghost-slot__label">
                                  + {label}
                                </span>
                              </button>
                            ))}

                            {resourceBlocks.map((block) => (
                              <div
                                key={block.id}
                                className="cd-agenda-matrix__manual-block"
                                style={{
                                  top: blockTopPx(block, overview.timeZone),
                                  height: blockHeightPx(block),
                                }}
                                title={
                                  block.visibleLabel ??
                                  block.reason ??
                                  "Bloqueo"
                                }
                              >
                                <span className="cd-agenda-matrix__manual-block-label">
                                  {block.visibleLabel ?? "Bloqueo"}
                                </span>
                                {block.reason && (
                                  <span className="cd-agenda-matrix__manual-block-reason">
                                    {block.reason}
                                  </span>
                                )}
                              </div>
                            ))}

                            {items.map((item) => (
                              <AppointmentButton
                                key={item.id}
                                item={item}
                                timeZone={overview.timeZone}
                                mode="vertical"
                                onOpen={() => {
                                  setAlternatives([]);
                                  setSelectedAppointment(item);
                                }}
                              />
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loading && view === "week" && (
            <div className="cd-agenda-week">
              {weekKeys.map((dateKey) => {
                const items = appointments.filter(
                  (item) =>
                    dateKeyInZone(item.startsAt, overview.timeZone) === dateKey,
                );
                return (
                  <section
                    key={dateKey}
                    className={`cd-agenda-week__col${
                      dateKey === currentDateKey
                        ? " cd-agenda-week__col--today"
                        : ""
                    }`}
                  >
                    <header className="cd-agenda-week__head">
                      {displayDate(dateKey)}
                    </header>
                    <div className="cd-agenda-week__items">
                      {items.length === 0 ? (
                        <button
                          type="button"
                          className="cd-agenda-week__empty"
                          onClick={() => {
                            setSelectedDate(dateKey);
                            openCreate(null, null);
                          }}
                        >
                          Sin turnos · crear
                        </button>
                      ) : (
                        items.map((item) => (
                          <button
                            type="button"
                            key={item.id}
                            className={`cd-agenda-week__item ${statusModifier(
                              item.status,
                              "cd-agenda-block",
                            )}`}
                            onClick={() => setSelectedAppointment(item)}
                          >
                            <span className="cd-agenda-week__time">
                              {timeInZone(item.startsAt, overview.timeZone)}
                            </span>
                            <span className="cd-agenda-week__name">
                              {item.customerName}
                            </span>
                            <span className="cd-agenda-week__svc">
                              {item.serviceName}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  </section>
                );
              })}
            </div>
          )}

          {!loading && view === "list" && (
            <div className="cd-agenda-list">
              <div className="cd-agenda-list__header" aria-hidden="true">
                <span>Fecha</span>
                <span>Hora</span>
                <span>Cliente</span>
                <span>Servicio</span>
                <span>Profesional</span>
                <span>Estado</span>
              </div>
              {listAppointments.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  className={`cd-agenda-list__row ${statusModifier(
                    item.status,
                    "cd-agenda-block",
                  )}`}
                  onClick={() => setSelectedAppointment(item)}
                >
                  <span className="cd-agenda-list__origin">
                    {displayDate(
                      dateKeyInZone(item.startsAt, overview.timeZone),
                    )}
                  </span>
                  <span className="cd-agenda-list__time">
                    {timeInZone(item.startsAt, overview.timeZone)}
                  </span>
                  <span className="cd-agenda-list__name">
                    {item.customerName}
                  </span>
                  <span className="cd-agenda-list__service">
                    {item.serviceName}
                  </span>
                  <span className="cd-agenda-list__prof">
                    {item.professionalName ?? "Sin asignar"}
                  </span>
                  <span className="cd-agenda-list__status">
                    {statusLabel(item.status)}
                  </span>
                </button>
              ))}
              {listAppointments.length === 0 && (
                <p className="cd-agenda-week__empty">
                  Sin turnos en este rango.
                </p>
              )}
            </div>
          )}

          {!loading && view === "timeline" && (
            <div className="cd-agenda-timeline">
              {dayColumns.length === 1 && dayColumns[0] === null && (
                <p className="cd-agenda-timeline__empty">
                  Configurá profesionales o recursos para ver utilización.
                </p>
              )}
              <div className="cd-agenda-timeline__scroll">
                <div className="cd-agenda-timeline__grid">
                  <div className="cd-agenda-timeline__head-row">
                    <div className="cd-agenda-timeline__corner">Recursos</div>
                    <div
                      className="cd-agenda-timeline__hours"
                      style={{ width: timelineTrackWidth }}
                    >
                      {slots.slice(0, -1).map((label) => (
                        <div
                          key={label}
                          className="cd-agenda-timeline__tick"
                          style={{ width: TIMELINE_SLOT_WIDTH_PX }}
                        >
                          {label}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="cd-agenda-timeline__body">
                    {activeResources.map((resource) => {
                      const items = selectedDayAppointments.filter((item) =>
                        appointmentUsesResource(item, resource.id),
                      );
                      return (
                        <div
                          key={resource.id}
                          className="cd-agenda-timeline__row"
                        >
                          <div className="cd-agenda-timeline__row-head">
                            {resource.displayName ?? resource.name}
                          </div>
                          <div
                            className="cd-agenda-timeline__track"
                            style={{ width: timelineTrackWidth }}
                          >
                            {items.map((item) => (
                              <AppointmentButton
                                key={item.id}
                                item={item}
                                timeZone={overview.timeZone}
                                mode="horizontal"
                                onOpen={() => setSelectedAppointment(item)}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="cd-agenda-mobile-list" aria-label="Turnos del día">
            {appointments
              .filter(
                (item) =>
                  view === "week" ||
                  view === "list" ||
                  dateKeyInZone(item.startsAt, overview.timeZone) ===
                    selectedDate,
              )
              .map((item) => (
                <button
                  type="button"
                  key={item.id}
                  className={`cd-agenda-mobile-card ${statusModifier(
                    item.status,
                    "cd-agenda-block",
                  )}`}
                  onClick={() => setSelectedAppointment(item)}
                >
                  <div className="cd-agenda-mobile-card__head">
                    <span className="cd-agenda-mobile-card__time">
                      {timeInZone(item.startsAt, overview.timeZone)}
                    </span>
                    <span className="cd-agenda-mobile-card__status">
                      {statusLabel(item.status)}
                    </span>
                  </div>
                  <p className="cd-agenda-mobile-card__name">
                    {item.customerName}
                  </p>
                  <p className="cd-agenda-mobile-card__service">
                    {item.serviceName}
                  </p>
                  {item.professionalName && (
                    <p className="cd-agenda-mobile-card__staff">
                      Con {item.professionalName}
                    </p>
                  )}
                  <p className="cd-agenda-mobile-card__meta">
                    {displayDate(
                      dateKeyInZone(item.startsAt, overview.timeZone),
                    )}{" "}
                    · {item.locationName ?? "Sin local"}
                  </p>
                </button>
              ))}
            {!loading && appointments.length === 0 && (
              <div className="cd-agenda-mobile-empty">
                Sin turnos para mostrar.
              </div>
            )}
          </div>
        </section>
      </div>

      {selectedAppointment && (
        <AppointmentDrawer
          key={`${selectedAppointment.id}:${selectedAppointment.status}`}
          item={selectedAppointment}
          organizationId={organizationId}
          api={api}
          depositPolicy={agenda?.depositPolicy ?? { mode: "none", value: 0 }}
          timeZone={overview.timeZone}
          busy={operationBusy}
          error={error}
          alternatives={alternatives}
          basePath={basePath}
          navigate={navigate}
          onClose={() => {
            setSelectedAppointment(null);
            setError(null);
            setAlternatives([]);
            if (requestedAppointmentId) {
              navigate(`${basePath}/agenda`);
            }
          }}
          onReschedule={(startsAt) =>
            reschedule(selectedAppointment, startsAt)
          }
          onCancel={() => cancel(selectedAppointment)}
          onTransition={(status) =>
            transition(selectedAppointment, status)
          }
          onSaveNotes={(notes, expectedRevision) =>
            saveAppointmentNotes(
              selectedAppointment,
              notes,
              expectedRevision,
            )
          }
        />
      )}

      {createOpen && agenda && (
        <NewAppointmentDrawer
          key={`${selectedDate}:${prefillTime ?? "none"}:${
            prefillResourceId ?? "any"
          }:${initialCustomerId ?? "walk-in"}`}
          organizationId={organizationId}
          overview={overview}
          agenda={agenda}
          api={api}
          timeZone={overview.timeZone}
          initialDate={selectedDate}
          initialTime={prefillTime}
          initialResourceId={prefillResourceId}
          initialCustomerId={initialCustomerId}
          busy={operationBusy}
          error={error}
          alternatives={alternatives}
          onClose={() => {
            setCreateOpen(false);
            setError(null);
            setAlternatives([]);
            if (initialCustomerId) {
              navigate(`${basePath}/agenda`);
            }
          }}
          onCreate={createAppointment}
        />
      )}

      {blockOpen && agenda && (
        <NewBlockDrawer
          organizationId={organizationId}
          agenda={agenda}
          api={api}
          timeZone={overview.timeZone}
          initialDate={selectedDate}
          initialTime={prefillTime}
          initialResourceId={prefillResourceId}
          busy={operationBusy}
          error={error}
          onClose={() => {
            setBlockOpen(false);
            setError(null);
          }}
          onCreated={async () => {
            setBlockOpen(false);
            await loadAgenda();
          }}
        />
      )}
    </section>
  );
}
